// SocialBar — D39/D40 喜欢 / 不喜欢 / 转存 三连组件。
//
// 详见:
//   - docs/database/social.md(D40 counter + Redis 设计)
//   - docs/decisions/decided.md §3.2 D39 / D40
//   - pen frame zkY66 / kENKt / qCOtS / E56sH(SocialBar UI)
//
// 行为:
//   - 喜欢 / 不喜欢:VIP 用户点击 → 调 toggle_like / toggle_dislike;非 VIP 弹"升级 VIP"
//   - 转存:调用方注入 onTransfer 回调(file 走 save_to_my_pan;pack 走 share/<id>/save-to-pan)
//   - 状态可控:既支持受控(传 stats / state),也支持非受控(自行 fetch)

import { useState } from 'react'

export type SocialBarStats = {
  like_count: number
  dislike_count: number
  i_liked?: boolean | null
  i_disliked?: boolean | null
}

export type SocialBarProps = {
  // 已展示的 count + 个人状态(由父组件 fetch)
  stats: SocialBarStats | null
  // 父组件回调
  onLike: () => void | Promise<void>
  onDislike: () => void | Promise<void>
  // 转存(可选):pack / file 预览传入显示「转存」按钮;article 详情不传 → 隐藏转存
  onTransfer?: () => void | Promise<void>
  // 加载中 / 禁用
  isPending?: boolean
  isVip?: boolean
  // 非 VIP 点 like / dislike 触发(由父组件弹 modal)
  onVipRequired?: () => void
  // 主题(dark = media 预览深色背景;light = 资源包浅色)
  theme?: 'light' | 'dark'
}

export function SocialBar(props: SocialBarProps) {
  const { stats, onLike, onDislike, onTransfer, isPending, isVip = true, onVipRequired, theme = 'dark' } = props
  const [busy, setBusy] = useState(false)

  const handleVote = (kind: 'like' | 'dislike') => async () => {
    if (!isVip) {
      onVipRequired?.()
      return
    }
    if (busy || isPending) return
    try {
      setBusy(true)
      if (kind === 'like') await onLike()
      else await onDislike()
    } finally {
      setBusy(false)
    }
  }

  const handleTransfer = async () => {
    if (!onTransfer || busy || isPending) return
    try {
      setBusy(true)
      await onTransfer()
    } finally {
      setBusy(false)
    }
  }

  const likeActive = stats?.i_liked === true
  const dislikeActive = stats?.i_disliked === true
  const colorIdle = theme === 'dark' ? '#9CA3AF' : '#6B7280'
  const colorActive = '#19caad'

  return (
    <div className="flex items-center justify-around px-5 py-3" data-testid="social-bar">
      <SocialBtn
        icon="heart-up"
        label={formatCount(stats?.like_count)}
        active={likeActive}
        colorIdle={colorIdle}
        colorActive={colorActive}
        onClick={handleVote('like')}
        aria-label="喜欢"
      />
      <SocialBtn
        icon="heart-down"
        label={formatCount(stats?.dislike_count)}
        active={dislikeActive}
        colorIdle={colorIdle}
        colorActive={colorActive}
        onClick={handleVote('dislike')}
        aria-label="不喜欢"
      />
      {onTransfer && (
        <SocialBtn
          icon="transfer"
          label="转存"
          active={false}
          colorIdle={colorIdle}
          colorActive={colorActive}
          onClick={handleTransfer}
          aria-label="转存"
        />
      )}
    </div>
  )
}

function formatCount(n: number | undefined): string {
  if (n == null) return '—'
  if (n >= 10000) return `${(n / 1000).toFixed(1)}k`
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`
  return String(n)
}

function SocialBtn({
  icon,
  label,
  active,
  colorIdle,
  colorActive,
  onClick,
  ...rest
}: {
  icon: 'heart-up' | 'heart-down' | 'transfer'
  label: string
  active: boolean
  colorIdle: string
  colorActive: string
  onClick: () => void
} & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  const stroke = active ? colorActive : colorIdle
  const common = {
    width: 22,
    height: 22,
    viewBox: '0 0 24 24',
    fill: active ? colorActive : 'none',
    stroke,
    strokeWidth: 1.8,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  }
  return (
    <button type="button" onClick={onClick} className="flex flex-col items-center gap-1" style={{ color: stroke }} {...rest}>
      {icon === 'heart-up' && (
        <svg {...common}>
          {/* 上箭头 + 心:用 thumbs-up like icon */}
          <path d="M7 11v8a2 2 0 002 2h7a2 2 0 002-1.7l1-6a2 2 0 00-2-2.3h-4l.6-3a2 2 0 00-3.4-1.7L7 11z" />
          <path d="M3 11h4v10H3z" />
        </svg>
      )}
      {icon === 'heart-down' && (
        <svg {...common}>
          {/* 下箭头 thumbs-down */}
          <path d="M17 13V5a2 2 0 00-2-2H8a2 2 0 00-2 1.7l-1 6a2 2 0 002 2.3h4l-.6 3a2 2 0 003.4 1.7L17 13z" />
          <path d="M21 13h-4V3h4z" />
        </svg>
      )}
      {icon === 'transfer' && (
        <svg {...common}>
          <path d="M3 12v4a2 2 0 002 2h14a2 2 0 002-2v-4" />
          <path d="M7 8l5-5 5 5" />
          <path d="M12 3v12" />
        </svg>
      )}
      <span className="text-[11px]">{label}</span>
    </button>
  )
}
